import { useState, useEffect } from "react";
import {
  Layers,
  TrendingUp,
  TrendingDown,
  ShieldAlert,
  ShieldCheck,
  Zap,
  Gauge,
  Activity,
  Moon,
  Clock,
} from "lucide-react";
import { fetchKisMetrics } from "../api/client";
import type {
  KisMetricsResponse,
  KisDisparityItem,
  KisTickItem,
  KisOrderBookItem,
  KisProgramItem,
  KisVkospiItem,
} from "../api/types";

function fmtCurrency(val?: number): string {
  if (val === undefined || val === null || isNaN(val)) return "—";
  return val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtWon(val?: number): string {
  if (val === undefined || val === null || isNaN(val)) return "—";
  return `${Math.round(val).toLocaleString()}원`;
}

function fmtBps(bps?: number): string {
  if (bps === undefined || bps === null || isNaN(bps)) return "—";
  const sign = bps > 0 ? "+" : "";
  return `${sign}${bps.toFixed(1)} bps`;
}

function fmtAmtBillion(amt?: number): string {
  if (amt === undefined || amt === null || isNaN(amt)) return "—";
  const sign = amt > 0 ? "+" : "";
  const billion = amt / 100_000_000; // 억 원 단위
  return `${sign}${billion.toFixed(1)}억`;
}

interface KrxSessionInfo {
  isRegularMarket: boolean;
  statusText: string;
  badgeClass: string;
  desc: string;
}

function getKrxSessionInfo(): KrxSessionInfo {
  const now = new Date();
  const utc = now.getTime() + now.getTimezoneOffset() * 60000;
  const kst = new Date(utc + 9 * 3600000);
  const day = kst.getDay(); // 0: Sun, 6: Sat
  const hour = kst.getHours();
  const minute = kst.getMinutes();
  const timeVal = hour * 60 + minute;

  const isWeekend = day === 0 || day === 6;
  const isRegularMarket = !isWeekend && timeVal >= 540 && timeVal <= 930; // 09:00 ~ 15:30 KST

  if (isWeekend) {
    return {
      isRegularMarket: false,
      statusText: "KRX 주말 휴장 (암호화폐 24/7 감시)",
      badgeClass: "badge-yellow",
      desc: "주말 휴장 · 바이낸스 퍼프 감시 모드",
    };
  }
  if (isRegularMarket) {
    return {
      isRegularMarket: true,
      statusText: "KRX 정규장 개장 중 (09:00~15:30)",
      badgeClass: "badge-green",
      desc: "실시간 틱·호가 OBI 연동 중",
    };
  }
  return {
    isRegularMarket: false,
    statusText: "KRX 정규장 마감 (야간 세션)",
    badgeClass: "badge-blue",
    desc: "직전 종가 기준 유지 · 바이낸스/토스 야간 관제",
  };
}

export default function KisObservabilityCard({ advisoryOnly = false }: { advisoryOnly?: boolean }) {
  const [data, setData] = useState<KisMetricsResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchKisMetrics()
      .then((res) => setData(res))
      .catch((err) => console.error("Failed to load KIS metrics", err))
      .finally(() => setLoading(false));

    const timer = setInterval(() => {
      fetchKisMetrics()
        .then((res) => setData(res))
        .catch(() => {});
    }, 5000); // 5s real-time refresh
    return () => clearInterval(timer);
  }, []);

  if (loading && !data) {
    return (
      <div className="card">
        <div className="card-title-row">
          <div className="card-title">
            <Layers size={16} />
            <span>한국투자증권(KIS) 딥-퀀트 미시구조 관제</span>
          </div>
        </div>
        <p className="muted-note">KIS 실시간 마켓 데이터를 수신하는 중입니다...</p>
      </div>
    );
  }

  const disparity = data?.disparity || {};
  const ticks = data?.ticks || {};
  const orderbooks = data?.orderbooks || {};
  const programs = data?.programs || {};
  const vkospi: KisVkospiItem = data?.vkospi || {};
  const hasIv = typeof vkospi.current_iv === "number" && vkospi.current_iv > 0;

  const koruDisp: KisDisparityItem | undefined = disparity["KORUUSDT"];
  const skhyDisp: KisDisparityItem | undefined = disparity["SKHYUSDT"];
  const session = getKrxSessionInfo();

  const getRegimeBadge = (regime?: string) => {
    switch (regime?.toUpperCase()) {
      case "OVERPRICED":
        return <span className="badge badge-yellow">과열 프리미엄 (숏 우위)</span>;
      case "UNDERPRICED":
        return <span className="badge badge-blue">과도한 디스카운트 (롱 우위)</span>;
      case "FAIR":
      default:
        return <span className="badge badge-green">정상 수렴 범위</span>;
    }
  };

  const renderDisparityTile = (
    title: string,
    sub: string,
    item?: KisDisparityItem,
  ) => {
    if (!item) {
      return (
        <div className="metric-tile" style={{ padding: "var(--space-3)" }}>
          <div className="metric-tile-head">
            <span className="metric-tile-label">{title} ({sub})</span>
          </div>
          <div className="metric-tile-value">—</div>
          <div className="metric-tile-sub">괴리율 데이터 대기</div>
        </div>
      );
    }

    const bps = item.disparity_bps;
    const isOver = bps > 50;
    const isUnder = bps < -50;
    const verified = item.model_verified === true;
    const color = !verified ? "var(--text-muted)" : isOver ? "var(--yellow)" : isUnder ? "var(--accent)" : "var(--green)";

    return (
      <div className="metric-tile" style={{ padding: "var(--space-3)" }}>
        <div className="metric-tile-head" style={{ marginBottom: "var(--space-2)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)" }}>
            <span style={{ fontWeight: 600, fontSize: "var(--text-sm)" }}>
              {title}
            </span>
            <span className="muted-note" style={{ fontSize: "var(--text-2xs)" }}>
              {sub}
            </span>
          </div>
          {verified ? getRegimeBadge(item.regime) : <span className="badge badge-yellow">합성 NAV 미검증</span>}
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <div>
            <div className="muted-note" style={{ fontSize: "var(--text-2xs)" }}>선물가 vs 합성 NAV 원자료</div>
            <div className="tabular-nums" style={{ fontSize: "var(--text-sm)", fontWeight: 700 }}>
              ${fmtCurrency(item.binance_price)} <span className="muted-note" style={{ fontWeight: 400 }}>/ ${fmtCurrency(item.synthetic_nav)}</span>
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div className="muted-note" style={{ fontSize: "var(--text-2xs)" }}>기초자산 괴리율</div>
            <div className="tabular-nums" style={{ fontSize: "var(--text-base)", fontWeight: 700, color }}>
              {fmtBps(item.disparity_bps)}
            </div>
          </div>
        </div>
        {!verified && <p className="muted-note">{item.quality_note || '기초 바스켓·가격 배율 미검증'} · 입력 {item.input_state || 'legacy_unverified'} · {item.input_basis || '기존 입력 근거 미확인'}</p>}
      </div>
    );
  };

  const renderMicroTile = (
    name: string,
    code: string,
    tick?: KisTickItem,
    book?: KisOrderBookItem,
    prog?: KisProgramItem,
  ) => {
    const obi = book?.obi || 0;
    const validCttr = tick?.cttr && tick.cttr > 0 && tick.cttr <= 500 ? tick.cttr : null;
    const obiPct = Math.min(100, Math.max(-100, Math.round(obi * 100)));
    const isBuyHeavy = obi > 0.15;
    const isSellHeavy = obi < -0.15;

    return (
      <div className="metric-tile" style={{ padding: "var(--space-3)" }}>
        <div className="metric-tile-head" style={{ marginBottom: "var(--space-2)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)" }}>
            <span style={{ fontWeight: 600, fontSize: "var(--text-sm)" }}>
              {name} ({code})
            </span>
            <span className="tabular-nums" style={{ fontWeight: 700, fontSize: "var(--text-sm)" }}>
              {fmtWon(tick?.price)}
            </span>
          </div>
          <div style={{ display: "flex", gap: "var(--space-1)" }}>
            {validCttr !== null ? (
              <span className={`badge ${validCttr >= 100 ? "badge-green" : "badge-red"}`}>
                체결강도 {validCttr.toFixed(1)}%
              </span>
            ) : (
              <span className="badge badge-blue">
                실시간 체결
              </span>
            )}
          </div>
        </div>

        {/* 10단계 호가 불균형 (OBI) 비주얼 바 */}
        <div style={{ marginTop: "var(--space-2)", marginBottom: "var(--space-3)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: "var(--text-2xs)" }}>
            <span className="muted-note">10단계 호가 불균형 (OBI)</span>
            <span className="tabular-nums" style={{ fontWeight: 700, color: isBuyHeavy ? "var(--green)" : isSellHeavy ? "var(--red)" : "inherit" }}>
              {obi > 0 ? `+${obi.toFixed(2)} (매수우세)` : obi < 0 ? `${obi.toFixed(2)} (매도우세)` : "0.00 (균형)"}
            </span>
          </div>
          <div
            style={{
              height: 6,
              background: "var(--bg-secondary)",
              borderRadius: "var(--radius-full)",
              marginTop: 4,
              overflow: "hidden",
              position: "relative",
            }}
          >
            <div
              style={{
                position: "absolute",
                top: 0,
                bottom: 0,
                left: obi >= 0 ? "50%" : `${50 + obiPct / 2}%`,
                width: `${Math.abs(obiPct) / 2}%`,
                background: obi >= 0 ? "var(--green)" : "var(--red)",
                transition: "all var(--dur) var(--ease)",
              }}
            />
          </div>
        </div>

        {/* 비차익 프로그램 수급 & 모멘텀 */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--space-2)", background: "var(--bg-secondary)", padding: "var(--space-2)", borderRadius: "var(--radius-sm)" }}>
          <div>
            <div className="muted-note" style={{ fontSize: "var(--text-2xs)" }}>비차익 순매수</div>
            <div className="tabular-nums" style={{ fontSize: "var(--text-xs)", fontWeight: 700, color: (prog?.non_arb_net || 0) >= 0 ? "var(--green)" : "var(--red)" }}>
              {fmtAmtBillion(prog?.non_arb_net)}
            </div>
          </div>
          <div>
            <div className="muted-note" style={{ fontSize: "var(--text-2xs)" }}>수급 모멘텀</div>
            <div style={{ fontSize: "var(--text-xs)", fontWeight: 700, display: "flex", alignItems: "center", gap: 3 }}>
              {(prog?.momentum_score || 0) > 0 ? (
                <>
                  <TrendingUp size={12} color="var(--green)" />
                  <span style={{ color: "var(--green)" }}>매수 가속 ($d_1, d_2 &gt; 0$)</span>
                </>
              ) : (prog?.momentum_score || 0) < 0 ? (
                <>
                  <TrendingDown size={12} color="var(--red)" />
                  <span style={{ color: "var(--red)" }}>매도 가속</span>
                </>
              ) : (
                <span className="muted-note">중립</span>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="card" style={{ marginBottom: "var(--space-4)" }}>
      <div className="card-title-row">
        <div className="card-title">
          <Layers size={18} color="var(--accent)" />
          <span>한국투자증권(KIS) Open API 실시간 딥-퀀트 미시구조</span>
        </div>
        <div style={{ display: "flex", gap: "var(--space-2)", alignItems: "center" }}>
          <span className={`badge ${session.badgeClass}`} style={{ display: "flex", alignItems: "center", gap: 4 }}>
            {session.isRegularMarket ? <Zap size={12} /> : <Moon size={12} />}
            {session.statusText}
          </span>
          <span className="muted-note" style={{ fontSize: "var(--text-2xs)" }}>
            5초 자동 갱신
          </span>
        </div>
      </div>

      <p className="data-method-note">토스 매크로 입력 {data?.macro_inputs?.available ? `원/달러 ${fmtCurrency(data.macro_inputs.usdkrw)} · 코스피 현물 ${fmtCurrency(data.macro_inputs.kospi_spot)}pt` : '부재·지연으로 사용 불가'} · 합성 NAV는 기초 바스켓·기준점·KIS 가격 배율 검증이 필요합니다.</p>
      {/* 1. 합성 NAV 괴리율 모니터 (KORU & SKHY) */}
      <div style={{ marginBottom: "var(--space-3)" }}>
        <div style={{ fontSize: "var(--text-xs)", fontWeight: 600, color: "var(--muted)", marginBottom: "var(--space-2)", display: "flex", alignItems: "center", gap: 4 }}>
          <Gauge size={14} /> 합성 공정가치(Synthetic NAV) 괴리율 알파
        </div>
        <div className="grid grid-2">
          {renderDisparityTile("KORU 3X 레버리지", "MSCI Korea 25/50", koruDisp)}
          {renderDisparityTile("SK하이닉스 1X", "USD 환산 공정가치", skhyDisp)}
        </div>
      </div>

      {/* 2. KRX 10단계 호가 불균형 (OBI) 및 비차익 프로그램 매매 */}
      <div style={{ marginBottom: "var(--space-3)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "var(--space-2)" }}>
          <div style={{ fontSize: "var(--text-xs)", fontWeight: 600, color: "var(--muted)", display: "flex", alignItems: "center", gap: 4 }}>
            <Activity size={14} /> KRX 대형주 10단계 호가 불균형(OBI) 및 비차익 프로그램 수급
          </div>
          {!session.isRegularMarket && (
            <span className="muted-note" style={{ fontSize: "var(--text-2xs)", display: "flex", alignItems: "center", gap: 3 }}>
              <Clock size={11} /> {session.desc}
            </span>
          )}
        </div>
        <div className="grid grid-2">
          {renderMicroTile(
            "삼성전자",
            "005930",
            ticks["005930"],
            orderbooks["005930"],
            programs["005930"],
          )}
          {renderMicroTile(
            "SK하이닉스",
            "000660",
            ticks["000660"],
            orderbooks["000660"],
            programs["000660"],
          )}
        </div>
      </div>

      {/* 3. VKOSPI 옵션 내재변동성 사전 가드 */}
      <div
        style={{
          background: vkospi.is_spike ? "var(--red-soft)" : "var(--bg-secondary)",
          border: vkospi.is_spike ? "1px solid var(--red)" : "1px solid var(--border)",
          borderRadius: "var(--radius-sm)",
          padding: "var(--space-3)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)" }}>
          {vkospi.is_spike ? (
            <ShieldAlert size={18} color="var(--red)" />
          ) : (
            <ShieldCheck size={18} color="var(--green)" />
          )}
          <div>
            <div style={{ fontWeight: 600, fontSize: "var(--text-xs)", display: "flex", alignItems: "center", gap: 6 }}>
              <span>VKOSPI 옵션 내재변동성(IV) {advisoryOnly ? "참고 지표 · 현재 가격 기반 전략에 미적용" : "사전 리스크 가드"}</span>
              <span className={`badge ${!hasIv ? "badge-yellow" : vkospi.is_spike ? "badge-red" : "badge-green"}`}>
                {!hasIv ? "관측값 없음" : vkospi.is_spike ? "변동성 스파이크 감지" : "관측값 안정권"}
              </span>
            </div>
            <div className="muted-note tabular-nums" style={{ fontSize: "var(--text-2xs)", marginTop: 2 }}>
              {hasIv ? (
                <>현재 IV: {vkospi.current_iv?.toFixed(1)}% | EWMA: {vkospi.ewma_iv?.toFixed(1)}% | z-score: {vkospi.z_score?.toFixed(2)}</>
              ) : (
                <>현재 IV 관측값이 없어 안정 여부를 판정할 수 없습니다.</>
              )}
            </div>
          </div>
        </div>

        <div style={{ textAlign: "right" }}>
          <div className="muted-note" style={{ fontSize: "var(--text-2xs)" }}>{advisoryOnly ? "전략 적용" : "포지션 한도 배수"}</div>
          <div className="tabular-nums" style={{ fontWeight: 700, fontSize: "var(--text-sm)", color: vkospi.is_spike ? "var(--red)" : "var(--green)" }}>
            {advisoryOnly ? "미적용" : `${((vkospi.size_multiplier ?? 1.0) * 100).toFixed(0)}% (${vkospi.size_multiplier ?? 1.0}x)`}
          </div>
        </div>
      </div>
    </div>
  );
}
